import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, FileText, IdCard, Wallet, ShieldCheck, Lock, Server, EyeOff } from 'lucide-react';

const criteria = [
  'Be at least 18 years old (19 in some states)',
  'Be a U.S. citizen, permanent resident or hold a valid long-term visa',
  'Have a verifiable, regular source of income',
  'Hold an active checking account in your own name',
  'Provide a valid email address and phone number',
];

const documents = [
  { icon: IdCard, title: 'Proof of identity', desc: 'Government-issued photo ID such as a driver’s licence or passport.' },
  { icon: Wallet, title: 'Proof of income', desc: 'Recent pay stubs, bank statements or tax returns.' },
  { icon: FileText, title: 'Proof of address', desc: 'A utility bill or bank statement issued within the last 90 days.' },
];

const security = [
  { icon: Lock, title: '256-bit SSL encryption', desc: 'Every page and form is served over HTTPS, so your data is encrypted in transit.' },
  { icon: Server, title: 'Encrypted storage', desc: 'Personal and financial data is stored encrypted at rest with strict access controls.' },
  { icon: EyeOff, title: 'We never sell your data', desc: 'Your information is used to assess your application and service your loan — nothing else.' },
  { icon: ShieldCheck, title: 'Fraud monitoring', desc: 'Applications and accounts are monitored continuously for suspicious activity.' },
];

export default function EligibilitySecurity() {
  return (
    <>
      <section id="eligibility" className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
            <div>
              <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
                Eligibility
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                Who can apply
              </h2>
              <p className="text-muted-foreground mb-6">
                We assess every application individually, including your credit history and your ability
                to repay. Meeting the criteria below does not guarantee approval.
              </p>
              <ul className="space-y-3">
                {criteria.map((c) => (
                  <li key={c} className="flex gap-3 text-foreground">
                    <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-display text-xl font-bold text-foreground mb-5">
                Documents you may be asked for
              </h3>
              <div className="space-y-4">
                {documents.map((d) => (
                  <Card key={d.title} className="shadow-card">
                    <CardContent className="p-5 flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <d.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{d.title}</p>
                        <p className="text-sm text-muted-foreground">{d.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="security" className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              Security &amp; Privacy
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              How we protect your information
            </h2>
            <p className="text-muted-foreground text-lg">
              Bank-level safeguards protect your personal and financial details at every step.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {security.map((s) => (
              <Card key={s.title} className="shadow-card">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center mb-4">
                    <s.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-display font-semibold text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
